import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import IndustrialLayout from './IndustrialLayout';
import ReportDataMenu from '../../components/ReportDataMenu';

// ── Footfall Report ───────────────────────────────────────────────────────────
// Customers in / out / inside now, for counting lines configured with
// purpose='footfall' on the Object Intelligence page. Same detector and the same
// event log the Object Intelligence Report reads — only the presentation differs,
// because footfall answers a business question (how many customers, when) rather
// than an ops one (which crossing, with what evidence).
//
// "Inside now" is NOT in − out over the whole day. The backend logs a `reset`
// marker whenever the counted area has been empty for a while, and occupancy is
// counted from the last marker — so a day's missed or doubled crossings cannot
// drift the figure permanently.
//
// The figures come from /ui/footfall/summary, counted over the whole ledger. This
// page used to fetch the newest 5000 crossings and count them itself; on a busy
// entrance the day outgrew that window and "in today" went down as old rows fell out.

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;

export default function IndustrialFootfallPage() {
  const [cameras, setCameras] = useState([]);
  const [sum, setSum]         = useState(null);     // /ui/footfall/summary
  const [scope, setScope]     = useState(null);      // selected camera idx
  const [lineFilter, setLineFilter] = useState('');  // '' = every footfall line on the camera
  const [live, setLive]       = useState(true);
  const [imgUrl, setImgUrl]   = useState(null);
  const [hover, setHover]     = useState(null);      // hovered hour bucket
  const [asTable, setAsTable] = useState(false);     // hours as a table (a11y / precise read)
  const prevUrl = useRef(null);

  // Which (camera, line) pairs are footfall lines? Read from the saved config —
  // the crossing events themselves carry no purpose.
  const footfallLines = useMemo(() => {
    const out = [];
    cameras.forEach(c => {
      ((c.detector_config?.object_count?.lines) || []).forEach(L => {
        if ((L.purpose || 'object') === 'footfall' && L.line)
          out.push({ cam: c.idx, camName: c.name || `Camera ${c.idx}`, line: L.name,
                     occupancy: L.show_occupancy !== false });
      });
    });
    return out;
  }, [cameras]);
  const footfallCams = useMemo(() => {
    const seen = new Map();
    footfallLines.forEach(f => { if (!seen.has(f.cam)) seen.set(f.cam, f.camName); });
    return [...seen].map(([idx, name]) => ({ idx, name }));
  }, [footfallLines]);
  const camLines = footfallLines.filter(f => f.cam === scope).map(f => f.line);
  // "Inside now" / "Peak" only when every line being shown opted in — a gate into an
  // area the camera cannot see (turnstiles into an office) has no meaningful inside,
  // and walking it together with a shop line would mix the two.
  const showOccupancy = footfallLines
    .filter(f => f.cam === scope && (!lineFilter || f.line === lineFilter))
    .every(f => f.occupancy);

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json()).then(d => setCameras(d.cameras || [])).catch(() => {});
  }, []);
  // Land on the first camera that actually has a footfall line.
  useEffect(() => {
    if (scope == null && footfallCams.length) setScope(footfallCams[0].idx);
  }, [footfallCams, scope]);
  useEffect(() => { setLineFilter(''); }, [scope]);

  const load = useCallback(() => {
    if (scope == null) return;
    const q = lineFilter ? `&line=${encodeURIComponent(lineFilter)}` : '';
    apiFetch(`/ui/footfall/summary?cam=${scope}${q}`)
      .then(r => r.ok ? r.json() : null).then(d => d && setSum(d)).catch(() => {});
  }, [scope, lineFilter]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  // Live annotated frame (server-drawn boxes + the counting line).
  useEffect(() => {
    if (!live || scope == null) return;
    let alive = true;
    const tick = () => apiImageUrl(`/ui/cameras/${scope}/annotated?t=${Date.now()}`).then(u => {
      if (!alive) { URL.revokeObjectURL(u); return; }
      setImgUrl(u); if (prevUrl.current) URL.revokeObjectURL(prevUrl.current); prevUrl.current = u;
    }).catch(() => {});
    tick(); const iv = setInterval(tick, 1500);
    return () => { alive = false; clearInterval(iv); if (prevUrl.current) { URL.revokeObjectURL(prevUrl.current); prevUrl.current = null; } };
  }, [live, scope]);

  // ── derived figures ─────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const hours = sum?.hours || HOURS.map(h => ({ h, in: 0, out: 0 }));
    return {
      inside: sum?.inside ?? 0, peak: sum?.peak ?? 0, hours,
      inToday: sum?.in ?? 0, outToday: sum?.out ?? 0,
      inYday: sum?.in_yday ?? 0,
      lastReset: sum?.last_reset ? { ts: sum.last_reset } : null,
      busiest: sum?.busiest || { h: -1, in: 0 },
      delta: sum?.delta ?? null,
      any: !!(sum && (sum.in || sum.out)),
    };
  }, [sum]);

  const maxIn = Math.max(1, ...stats.hours.map(x => x.in));
  const camName = footfallCams.find(c => c.idx === scope)?.name || '';

  if (footfallCams.length === 0) {
    return (
      <IndustrialLayout eyebrow="Footfall" title="Footfall Report" subtitle="NO FOOTFALL LINES CONFIGURED">
        <div className="ind-card">
          <div className="ind-empty" style={{ padding: 28, lineHeight: 1.7 }}>
            <b>No footfall counting is set up yet.</b><br />
            Open <b>Object Intelligence Config</b>, draw a counting line across the entrance,
            and set <b>“What is this line for?”</b> to <b>People in / out — customers</b>.<br />
            Draw staff zones over the seats behind the counter so the people working there are
            never counted as customers.
          </div>
        </div>
      </IndustrialLayout>
    );
  }

  return (
    <IndustrialLayout eyebrow="Footfall" title="Footfall Report"
      subtitle={`${camName.toUpperCase()} · ${stats.inToday} IN TODAY`}>

      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span className="ind-eye">Entrance</span>
          <select value={scope ?? ''} onChange={e => setScope(Number(e.target.value))} style={sel}>
            {footfallCams.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name}</option>)}
          </select>
          {camLines.length > 1 && (
            <select value={lineFilter} onChange={e => setLineFilter(e.target.value)} style={sel}>
              <option value="">All entrances on this camera</option>
              {camLines.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          <button className={`ind-pill${live ? ' on' : ''}`} onClick={() => setLive(l => !l)}>
            {live ? '⏸ Pause' : '▶ Resume'}
          </button>
          <span style={{ flex: 1 }} />
          <ReportDataMenu kind="count" cam={scope} camName={camName} items={camLines} onChanged={load} />
        </div>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* LEFT — the numbers */}
          <div style={{ flex: '1 1 420px', minWidth: 300 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              {showOccupancy && <Hero label="INSIDE NOW" val={stats.inside} />}
              <Stat label="IN TODAY"  val={stats.inToday}  color="var(--ind-ok)" />
              <Stat label="OUT TODAY" val={stats.outToday} color="var(--ind-accent)" />
              {showOccupancy && <Stat label="PEAK TODAY" val={stats.peak} color="var(--ind-tx)" />}
            </div>
            <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '0 0 16px' }}>
              {stats.delta == null
                ? 'No crossings yesterday to compare against.'
                : <>{stats.inToday} customers in today · <b style={{ color: stats.delta >= 0 ? 'var(--ind-ok)' : 'var(--ind-accent)' }}>
                    {stats.delta >= 0 ? '▲' : '▼'} {Math.abs(stats.delta)}%
                  </b> vs {stats.inYday} at this point yesterday.</>}
              {stats.busiest.in > 0 && <> Busiest hour {hourLabel(stats.busiest.h)} with {stats.busiest.in}.</>}
            </p>

            {/* Hourly bars — one series (customers in), so the heading names it and
                no legend is needed. Hover a bar for its exact in/out. */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <span className="ind-eye">Customers in, by hour</span>
              <button className="ind-pill" style={{ padding: '2px 8px' }} onClick={() => setAsTable(t => !t)}>
                {asTable ? '▮ Chart' : '▤ Table'}
              </button>
            </div>

            {asTable ? (
              <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--ind-line)', borderRadius: 8, marginTop: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ textAlign: 'left', color: 'var(--ind-tx3)' }}>
                    <th style={th}>Hour</th><th style={th}>In</th><th style={th}>Out</th>
                  </tr></thead>
                  <tbody>
                    {stats.hours.filter(x => x.in || x.out).map(x => (
                      <tr key={x.h} style={{ borderTop: '1px solid var(--ind-line)' }}>
                        <td style={td}>{hourLabel(x.h)}</td><td style={td}>{x.in}</td><td style={td}>{x.out}</td>
                      </tr>
                    ))}
                    {!stats.any && <tr><td style={td} colSpan={3}>No crossings today yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ marginTop: 10 }} role="img"
                aria-label={`Customers in by hour. ${stats.inToday} today, busiest hour ${
                  stats.busiest.in > 0 ? hourLabel(stats.busiest.h) : 'none'}.`}>
                <div style={{ position: 'relative', height: 150, borderBottom: '1px solid var(--ind-line)' }}>
                  {/* recessive gridlines */}
                  {[0.25, 0.5, 0.75, 1].map(f => (
                    <div key={f} style={{ position: 'absolute', left: 0, right: 0, bottom: `${f * 100}%`,
                      borderTop: '1px dashed var(--ind-line)', opacity: 0.5 }} />
                  ))}
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                    {stats.hours.map(x => (
                      <div key={x.h} onMouseEnter={() => setHover(x)} onMouseLeave={() => setHover(null)}
                        style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end', cursor: 'default' }}>
                        <div style={{
                          width: '100%',
                          // a non-zero hour always stays visible
                          height: x.in ? `${Math.max(3, (x.in / maxIn) * 100)}%` : 1,
                          background: x.in ? 'var(--ind-ok)' : 'var(--ind-line)',
                          borderRadius: '4px 4px 0 0',
                          opacity: hover && hover.h !== x.h ? 0.45 : 1,
                          transition: 'opacity .12s',
                        }} />
                      </div>
                    ))}
                  </div>
                  {/* direct-label the peak only, never every bar */}
                  {stats.busiest.in > 0 && (
                    <div style={{ position: 'absolute', left: `${(stats.busiest.h / 24) * 100}%`,
                      bottom: `${(stats.busiest.in / maxIn) * 100}%`, transform: 'translate(-25%, -100%)',
                      fontSize: 11, fontFamily: 'var(--ind-mono)', color: 'var(--ind-tx2)' }}>
                      {stats.busiest.in}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                  {stats.hours.map(x => (
                    <div key={x.h} style={{ flex: 1, textAlign: 'center', fontSize: 9,
                      color: 'var(--ind-tx3)', fontFamily: 'var(--ind-mono)' }}>
                      {x.h % 3 === 0 ? String(x.h).padStart(2, '0') : ''}
                    </div>
                  ))}
                </div>
                <div style={{ height: 20, marginTop: 6, fontSize: 12, color: 'var(--ind-tx2)' }}>
                  {hover
                    ? <>{hourLabel(hover.h)} — <b style={{ color: 'var(--ind-ok)' }}>{hover.in} in</b> ·{' '}
                        <b style={{ color: 'var(--ind-accent)' }}>{hover.out} out</b></>
                    : <span style={{ color: 'var(--ind-tx3)' }}>Hover a bar for that hour’s exact counts.</span>}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — live frame */}
          <div style={{ flex: '1 1 340px', minWidth: 280 }}>
            <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--ind-line)' }}>
              {imgUrl
                ? <img src={imgUrl} alt="" style={{ width: '100%', display: 'block' }} />
                : <div className="ind-empty" style={{ padding: 40 }}>{live ? 'Loading live frame…' : 'Preview paused.'}</div>}
            </div>
            <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 8, lineHeight: 1.6 }}>
              People whose track starts in a staff zone are never counted.
              {!showOccupancy ? null : stats.lastReset
                ? <> Occupancy last reset to zero at {new Date(stats.lastReset.ts * 1000).toLocaleTimeString()},
                    when the area was empty — “inside now” counts from there.</>
                : <> “Inside now” counts from the last time the area was seen empty.</>}
            </p>
          </div>
        </div>
      </div>
    </IndustrialLayout>
  );
}

function Hero({ label, val }) {
  return (
    <div style={{ flex: '1 1 130px', padding: '14px 12px', borderRadius: 10,
      border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
      <div style={{ fontSize: 44, fontWeight: 800, fontFamily: 'var(--ind-mono)', lineHeight: 1, color: 'var(--ind-tx)' }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: '.12em', color: 'var(--ind-tx3)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

function Stat({ label, val, color }) {
  return (
    <div style={{ flex: '1 1 90px', padding: '14px 10px', borderRadius: 10, textAlign: 'center',
      border: '1px solid var(--ind-line)', background: 'var(--ind-ink)' }}>
      <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--ind-mono)', lineHeight: 1, color }}>{val}</div>
      <div style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--ind-tx3)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const th  = { padding: '6px 10px', fontWeight: 600 };
const td  = { padding: '6px 10px', fontFamily: 'var(--ind-mono)' };
